'use client';

import { bunja } from 'bunja';
import { createScopeFromContext } from 'bunja/react';
import { atom } from 'jotai';
import { atomWithMutation, atomWithQuery, queryClientAtom } from 'jotai-tanstack-query';
import { createContext } from 'react';

import { getUserPoolApi, getUserPoolEligibilityApi, postUserPoolJoinApi } from '@flipster/apis/v1/userpools';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import { convertDate } from '@flipster/utils/datetime';

import { authBunja } from '../shared/auth/state';
import { currentTimeBunja } from '../shared/current-time/current-time.state';

const userPoolQueryKey = 'getUserPool';
const userPoolEligibilityQueryKey = 'getUserPoolEligibility';

export const UserPoolIdContext = createContext<string>('');
const UserPoolIdScope = createScopeFromContext(UserPoolIdContext);

export const userPoolEligibilityBunja = bunja(() => {
  const { isLoggedInAtom } = bunja.use(authBunja);

  const userPoolEligibilityBaseAtom = atomWithQuery((get) => ({
    queryKey: [userPoolEligibilityQueryKey, get(isLoggedInAtom)],
    queryFn: getUserPoolEligibilityApi,
  }));

  const userPoolEligibilityAtom = atom((get) => get(userPoolEligibilityBaseAtom).data?.data);
  const userPoolEligibilityIsLoadingAtom = atom((get) => get(userPoolEligibilityBaseAtom).isLoading);

  return {
    userPoolEligibilityAtom,
    userPoolEligibilityIsLoadingAtom,
  };
});
userPoolEligibilityBunja.debugLabel = 'userPoolEligibilityBunja';

export const userPoolBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const { isLoggedInAtom } = bunja.use(authBunja);
  const { currentTimeAtom } = bunja.use(currentTimeBunja);
  const userPoolId = bunja.use(UserPoolIdScope);
  const userPoolBaseAtom = atomWithQuery((get) => ({
    queryKey: [userPoolQueryKey, userPoolId, get(isLoggedInAtom)],
    queryFn: () => getUserPoolApi(userPoolId),
    enabled: !!userPoolId,
    refetchOnMount: 'always',
  }));

  const userPoolDataAtom = atom((get) => get(userPoolBaseAtom).data?.data);
  const userPoolIsLoadingAtom = atom((get) => get(userPoolBaseAtom).isLoading);
  const userPoolErrorAtom = atom((get) => get(userPoolBaseAtom).error);
  const eventStatusAtom = atom((get) => {
    const userPool = get(userPoolDataAtom);
    const currentTime = get(currentTimeAtom);
    if (!userPool) return 'Ongoing';
    return getStatusType(userPool.startTime, userPool.endTime, currentTime);
  });

  const yourTicketsAtom = atom((get) => {
    const userPool = get(userPoolDataAtom);
    return userPool
      ? userPool.missions.reduce((sum, mission) => sum + (mission.complete ? mission.ticketCount : 0), 0)
      : 0;
  });

  const estTotalRewardAtom = atom((get) => {
    const userPool = get(userPoolDataAtom);
    return userPool ? userPool.missions.reduce((sum, mission) => sum + Number(mission.estReward), 0) : 0;
  });

  const yourTotalRewardAtom = atom((get) => {
    const userPool = get(userPoolDataAtom);
    return userPool
      ? userPool.missions.reduce((sum, mission) => sum + (mission.complete ? Number(mission.estReward) : 0), 0)
      : 0;
  });

  const postUserPoolJoinMutationAtom = atomWithMutation(() => ({
    mutationFn: () => postUserPoolJoinApi(userPoolId),
    onSuccess() {
      const queryClient = store.get(queryClientAtom);
      queryClient.invalidateQueries({ queryKey: [userPoolEligibilityQueryKey] });
      queryClient.invalidateQueries({ queryKey: [userPoolQueryKey, userPoolId] });
    },
  }));

  return {
    userPoolDataAtom,
    userPoolIsLoadingAtom,
    userPoolErrorAtom,

    yourTicketsAtom,
    yourTotalRewardAtom,
    eventStatusAtom,
    estTotalRewardAtom,

    currentTimeAtom,
    postUserPoolJoinMutationAtom,
  };
});
userPoolBunja.debugLabel = 'userPoolBunja';

export type EventStatus = 'Ongoing' | 'Upcoming' | 'Closed';

const getStatusType = (startTime: string, endTime: string, currentTime: number): EventStatus => {
  const now = new Date(currentTime);
  const start = convertDate(startTime);
  const end = convertDate(endTime);

  if (!start || !end) return 'Ongoing';

  if (now < start) return 'Upcoming';
  if (now > end) return 'Closed';
  return 'Ongoing';
};
